import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import {
  clearHistory,
  lookBack,
  selectAllies,
  selectGame,
  selectHistory,
  selectLiveGame,
  selectRoundEvents,
  selectSpectatingAs,
} from "src/store/game"
import { collapseRoundEvents, describeRoundEvent, enrichRoundEvents, sortRoundEvents } from "src/utils/roundEventWording"
import { pingCity } from "src/store/ui"
import ModalService from "src/modals"
import { TRAINING_GAME_ID } from "src/tutorial/scenario"
import { withCoordChips } from "src/components/CoordChip"

import "./RoundReport.css"

// Panels either side of this one (Forces, Comms) render even when empty, so the dock does not
// jump around as a round with nothing to report comes and goes. This one now does the same.
const VISIBLE_EVENTS = 5

// Wraps bare numbers in a mono/bold span, and every coordinate pair ("53-45") in a chip that
// selects the cell on the map (owner's R27). describeRoundEvent hands back a finished sentence,
// not structured data, so this is a display-only pass over the text.
const FIGURE_PATTERN = /(\d[\d-]*\d|\d)/g

const withFigures = (text) =>
  text.split(FIGURE_PATTERN).map((part, index) => {
    if (index % 2 !== 1) return part
    if (/^\d{1,3}-\d{1,3}$/.test(part)) return <span key={index}>{withCoordChips(part)}</span>
    return (
      <span key={index} className="round-event-figure">
        {part}
      </span>
    )
  })

const RoundReport = ({ held = false }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  // Only what happened in the round just processed. Without this a round changed the numbers and
  // said nothing about why, which is the one thing a tick-based game cannot afford.
  const liveEvents = useSelector(selectRoundEvents)
  // The board on screen — a past round's while looking back — and the live one, whose tick is
  // what "the latest" means.
  const game = useSelector(selectGame)
  const liveGame = useSelector(selectLiveGame)
  const history = useSelector(selectHistory)
  const allies = useSelector(selectAllies)
  /* A borrowed seat — a replay, or a live spectator watching through somebody — not the player
     looking back at a round of their own game, which is this panel's own doing and changes neither
     the stepper's visibility nor the default filter. */
  const reviewing = useSelector(selectSpectatingAs) != null
  const [expanded, setExpanded] = useState(false)
  // Mine, the team's, or both (owner's R23): in a 2v2 the report mixes what happened to you with
  // what happened to your partner, and used to say nothing about which was which.

  /* Browsing past rounds drives the whole board, not this panel alone (requirement 35, H1/H7): ‹ / ›
     put a past round's step into the store's `history`, which every panel draws, and "Round N" or
     › past the newest past step clears it. One clock, so every copy of this panel (the dock, the
     compact pane, the phone sheet) reads the same round. The steps come from /steps/ (fog-filtered
     server-side, last 60 rounds) and are cached by the `lookBack` thunk. */
  const currentTick = liveGame?.tick ?? 0
  const training = String(liveGame?.id) === String(TRAINING_GAME_ID)
  const shownTick = history?.tick ?? currentTick
  const isLive = history == null
  const goTo = (tick) => {
    const bounded = Math.max(1, Math.min(currentTick, tick))
    if (bounded >= currentTick) dispatch(clearHistory())
    else dispatch(lookBack({ tick: bounded }))
  }

  const loadingSteps = Boolean(history?.loading)
  // Looking back at a round whose board is gone (older than the retention window): the events went
  // with it, and the line says so rather than showing the live round's under a past heading.
  const historyAgedOut = !isLive && !loadingSteps && !history?.game
  // The starvation line reads what starved off the raw event (S22a) — the display shape carries
  // the total only, so the raw list rides alongside. While looking back both are the past board's,
  // which `selectGame` already is.
  const boardShown = isLive || Boolean(history?.game)
  const serverEvents = boardShown ? enrichRoundEvents(liveEvents, game?.metadata?.events) : []

  // A battle that ended this same round already has its finalization line, which carries the
  // whole story — the per-round battle line beside it adds nothing, so it goes; and so does the
  // other side's released cell (S22b). The rule lives in the wording module, shared with the digest.
  const deduplicated = collapseRoundEvents(serverEvents)
  // Whose event this is: mine when I am among its subjects, the team's when a teammate is and I
  // am not.
  const alliesInvolved = (event) =>
    allies.filter((ally) => (event.subjectIds || []).includes(ally.user.id))
  const isMine = (event) => event.concernsMe !== false
  // Every notification, always (owner, 2026-10-05): the ALL / MINE / TEAM filters went, and an
  // ally's line still names the ally it concerns. Mine first, then each ally's, then by type
  // (owner, 2026-10-07) — before the cut to VISIBLE_EVENTS, so the first lines are the viewer's.
  const events = sortRoundEvents(deduplicated, allies.map((ally) => ally.user.id))
  const visibleEvents = expanded ? events : events.slice(0, VISIBLE_EVENTS)
  const hiddenCount = events.length - visibleEvents.length

  return (
    <div className="round-report with-scroll-bar vertical">
      {/* The round selector, and nothing else above the list (owner, 2026-10-05: no "Last round"
          title, no filters, arrows big enough to explore with). Hidden in a replay: the view
          already has a round stepper across the top, and two clocks on one screen that can
          disagree is the shape of bug this repo keeps finding. */}
      {/* Basic training has no server to read past rounds from — the arrows asked for them and
          got a 401 — so it shows the round and no way back (owner, 2026-10-05). */}
      <div className={`round-report-nav round-selector${reviewing ? " hidden" : ""}`}>
        {!training && (
          <button
            type="button"
            className="round-nav-arrow"
            onClick={() => goTo(shownTick - 1)}
            disabled={shownTick <= Math.max(1, currentTick - 59)}
            aria-label={t("game.roundReport.older")}
          >
            ‹
          </button>
        )}
        <button
          type="button"
          className={`round-nav-label${isLive ? " live" : ""}`}
          onClick={() => dispatch(clearHistory())}
          title={isLive ? undefined : t("game.roundReport.backToLatest")}
        >
          {t("game.roundReport.roundN", { round: shownTick })}
        </button>
        {!training && (
          <button
            type="button"
            className="round-nav-arrow"
            onClick={() => goTo(shownTick + 1)}
            disabled={isLive}
            aria-label={t("game.roundReport.newer")}
          >
            ›
          </button>
        )}
      </div>
      {!isLive && loadingSteps && <p className="round-report-empty">{t("common.loading")}</p>}
      {!isLive && historyAgedOut && <p className="round-report-empty">{t("game.roundReport.agedOut")}</p>}

      {/* The round's lines wait for its show (requirement 36, M9): while the board's moves still
          play, nothing is said about them — not the last round's lines under this round's
          number, and not "no events". */}
      {loadingSteps || held ? null : events.length === 0 && !historyAgedOut ? (
        <p className="round-report-empty">
          {isLive ? t("game.roundReport.empty") : t("game.roundReport.emptyHistory")}
        </p>
      ) : (
        <>
          {visibleEvents.map((event, index) => {
            const { tone, text } = describeRoundEvent(event)
            // Starvation reads as an economy warning (amber), not a battle loss (red) — the tone
            // the wording util returns ("bad") does not make that distinction, so it is made here
            // from the event's own `type`, which the util's caller already has.
            const toneClass = event.type === "starvation" ? "warn" : tone
            // A teammate's event carries their name (R23); one that is also mine carries nothing,
            // since the wording already speaks in the first person.
            const who = !isMine(event) ? alliesInvolved(event).map((ally) => ally.user.username) : []
            const whoChip =
              who.length > 0 ? <span className="round-event-who">{who.join(" · ")}</span> : null
            // A line that is also a door: a battle opens its view, a closed battle its final
            // report. One branch, keyed by type, so the next clickable event adds a
            // mapping instead of a copy of this block.
            const doorFor = {
              // A battle's line opens the battle view pinned to the very round it narrates —
              // boards, sub-rounds and all — the same way a closed one opens its final report.
              battle: () => ({
                title: t("game.roundReport.openBattle"),
                open: () =>
                  ModalService.show("BattleModal", {
                    city: { col: event.column, row: event.row },
                    initialRound: event.battleRound || null,
                  }),
              }),
              // A closed battle opens the same battle view a live one gets, pinned to its final
              // round (owner's Q14): the board, the sub-rounds and the survivors — not the
              // bespoke before/after card it used to get.
              battle_ended: () => ({
                title: t("game.roundReport.openBattleReport"),
                open: () =>
                  ModalService.show("BattleModal", {
                    city: { col: event.column, row: event.row },
                    initialRound: event.rounds || 1,
                  }),
              }),
              // A survival newcomer's line points at it (SV12): the map travels to the crown the
              // wave landed on, with the cell under the action bar. The tour showed it for a
              // second and a half; this is where a player goes to look again.
              enemy_appeared: () => ({
                title: t("game.roundReport.showOnMap"),
                open: () => dispatch(pingCity({ col: event.column, row: event.row })),
              }),
            }[event.type]
            if (doorFor) {
              const door = doorFor()
              // A div with a button role rather than a <button>: the coordinate chips inside
              // the line are buttons of their own (R27), and a button may not contain a button.
              return (
                <div
                  key={`${event.type}-${event.column}-${event.row}-${index}`}
                  role="button"
                  tabIndex={0}
                  className={`round-event ${toneClass} round-event-click`}
                  data-event={event.type}
                  title={door.title}
                  onClick={door.open}
                  onKeyDown={(keyEvent) => {
                    if (keyEvent.key === "Enter" || keyEvent.key === " ") {
                      keyEvent.preventDefault()
                      door.open()
                    }
                  }}
                >
                  {whoChip}
                  {withFigures(text)}
                </div>
              )
            }
            return (
              <p
                key={`${event.type}-${event.column}-${event.row}-${index}`}
                className={`round-event ${toneClass}${who.length ? " team" : ""}`}
              >
                {whoChip}
                {withFigures(text)}
              </p>
            )
          })}
          {hiddenCount > 0 && (
            <button type="button" className="round-report-toggle" onClick={() => setExpanded(true)}>
              {t("game.roundReport.viewFull", { count: events.length })}
            </button>
          )}
          {expanded && events.length > VISIBLE_EVENTS && (
            <button type="button" className="round-report-toggle" onClick={() => setExpanded(false)}>
              {t("game.roundReport.showFewer")}
            </button>
          )}
        </>
      )}
    </div>
  )
}

export default RoundReport
